import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useTenantContext } from '@/components/tenant/tenant-context'
import { EntryScreen, FullPageStatus, SignedInFooter } from '@/components/visitone/entry-screen'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { ProductMark } from '@/components/visitone/product-mark'
import { useT } from '@/components/visitone/visitone-provider'
import { signOut } from '@/lib/auth/session'

/*
 * Fails closed until the caller's access in the current tenant has loaded
 * (VIS-329 AC 9): no shell, rail, search or tenant data mounts before it. A
 * failure offers exactly Try again and Log out. Sits inside TenantProvider;
 * an access failure during a switch is the switch's own failed state instead.
 */
export function AccessGate({ children }: { children: React.ReactNode }) {
  const t = useT()
  const { user, logout } = useAuth0()
  const { accessPhase, reloadAccess, currentTenant, access } = useTenantContext()
  const onLogout = React.useCallback(() => void signOut(logout), [logout])
  const failure = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (accessPhase === 'error') failure.current?.focus()
  }, [accessPhase])

  if (accessPhase === 'error') {
    return (
      <EntryScreen wide>
        <ProductMark />
        <div ref={failure} tabIndex={-1} className="outline-none">
          <OperationFeedback
            state="error"
            title={t('core.access.loadErrorTitle')}
            detail={t('core.access.loadErrorBody', { tenant: currentTenant?.name ?? '' })}
            onRetry={reloadAccess}
          />
        </div>
        <SignedInFooter email={user?.email ?? user?.name} onLogout={onLogout} />
      </EntryScreen>
    )
  }

  if (accessPhase === 'loading' || !access) return <FullPageStatus text={t('core.access.loading')} />

  return <>{children}</>
}
